import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi, startSessionTimeout, mapRoleNamesToReduxRole } from '../services/api';
import { store } from '../store/store';
import { setUser as setReduxUser } from '../store/slices/authSlice';
import Logo from '../components/Logo';
import { webAuthnApi } from '../services/webauthn';
import useAuthStore from '../store/authStore';
import { Fingerprint } from 'lucide-react';
import { bufferToBase64URLString } from '../utils/base64Utils';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';

// Utility function to detect iPad devices
const isIPad = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPadOS = /ipad/.test(userAgent);
  const isMacWithTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isSafariOnIPad = /safari/.test(userAgent) && !/chrome/.test(userAgent) && navigator.maxTouchPoints > 1;
  
  return isIPadOS || isMacWithTouch || isSafariOnIPad;
};

function saveRecentCompany(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return;
  try {
    const existing: string[] = JSON.parse(localStorage.getItem('recentCompanies') || '[]');
    const updated = [trimmed, ...existing.filter(c => c.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    localStorage.setItem('recentCompanies', JSON.stringify(updated));
  } catch {}
}

export default function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [biometricsSupported, setBiometricsSupported] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [credentials, setCredentials] = useState(() => ({
    companyName: localStorage.getItem('lastCompanyName') || '',
    email: '',
    password: '',
  }));
  const [recentCompanies] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('recentCompanies') || '[]'); } catch { return []; }
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.message === 'Valid Session' && data.data) {
            const d = data.data;
            setUser({
              id: d.userID,
              companyID: d.companyID,
              companyName: d.companyName,
              email: d.email,
              firstName: d.firstName,
              lastName: d.lastName,
              roles: Array.isArray(d.roles) ? d.roles : [],
            });
            store.dispatch(
              setReduxUser({
                id: d.userID,
                companyID: String(d.companyID),
                companyName: d.companyName,
                email: d.email,
                firstName: d.firstName,
                lastName: d.lastName,
                roles: mapRoleNamesToReduxRole(d.roles),
              })
            );
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, [navigate, setUser]);

  useEffect(() => {
    // Check if WebAuthn is supported
    const checkBiometricsSupport = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricsSupported(available);
        }
      } catch (error) {
        console.error('Error checking biometrics support:', error);
      }
    };

    checkBiometricsSupport();
  }, []);

  const handleCompanyBlur = async () => {
    if (!credentials.companyName.trim()) return;
    try {
      const res = await fetch(
        `https://auth.ts1cloud.com/auth/company-mfa-check?company=${encodeURIComponent(credentials.companyName)}`,
        { credentials: 'include', headers: { 'Accept': 'application/json' } }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.requiresMfa) {
          navigate(`/login2?company=${encodeURIComponent(credentials.companyName)}`);
        }
      }
    } catch {
      // Silently ignore — don't block login if the check fails
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const attempt = async () => {
      const response = await authApi.login(credentials);
      localStorage.setItem('lastCompanyName', credentials.companyName);
      saveRecentCompany(credentials.companyName);
      await new Promise(resolve => setTimeout(resolve, 100));
      await authApi.verifyAccess();
      navigate('/', { replace: true });
    };

    try {
      await attempt();
    } catch {
      // Cold-start 500: Worker warms up on first hit — retry once after a short delay.
      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        await attempt();
      } catch (err) {
        try {
          await logAuditEvent(
            AUDIT_ACTIONS.USER_LOGIN,
            `Failed login attempt - Company: ${credentials.companyName}, Email: ${credentials.email}`
          );
        } catch (auditError) {
          console.error('Failed to log audit event:', auditError);
        }
        setError('Invalid credentials');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      // For iPads, check platform authenticator availability first
      if (isIPad()) {
        try {
          const isPlatformAuthenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          if (!isPlatformAuthenticatorAvailable) {
            setError('Face ID or Touch ID is not available on this iPad. This could be due to:\n\n1. Biometric authentication is disabled in Settings\n2. No biometric credentials are set up\n3. Hardware issue with Face ID/Touch ID\n4. iOS version compatibility issue\n\nPlease check Settings > Face ID & Passcode (or Touch ID & Passcode) and ensure biometric authentication is enabled.');
            setIsLoading(false);
            return;
          }
        } catch {
          // Non-fatal — continue and let WebAuthn itself surface the error
        }
      }

      // Warm up the auth Worker (may be cold on first biometric tap)
      try {
        await Promise.race([
          fetch('https://auth.ts1cloud.com/auth/verifyAccess', { method: 'GET', credentials: 'include' }),
          new Promise(resolve => setTimeout(resolve, 3000))
        ]);
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch { /* ignore — warmup is best-effort */ }

      // Get authentication options from server
      const options = await webAuthnApi.getAuthenticationOptions();

      const credential = await navigator.credentials.get({
        publicKey: {
          ...options,
          // Add extensions for better iPad support
          extensions: {
            appid: window.location.origin
          }
        }
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('No credentials received from browser');
      }

      const preparedCredential = webAuthnApi.prepareAuthenticationCredential(credential);
      const verificationResponse = await webAuthnApi.verifyAuthentication(preparedCredential);

      if (verificationResponse?.verified) {
        // Wait for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Verify session and get user data
        const sessionResponse = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!sessionResponse.ok) {
          throw new Error('Failed to verify session');
        }

        const sessionData = await sessionResponse.json();

        if (sessionData.message === 'Valid Session' && sessionData.data) {
          const d = sessionData.data;
          const userData = {
            id: d.userID,
            companyID: d.companyID,
            companyName: d.companyName,
            email: d.email,
            firstName: d.firstName,
            lastName: d.lastName,
            username: d.username,
            roles: Array.isArray(d.roles) ? d.roles : [],
          };

          setUser(userData);
          store.dispatch(
            setReduxUser({
              id: d.userID,
              companyID: String(d.companyID),
              companyName: d.companyName,
              email: d.email,
              firstName: d.firstName,
              lastName: d.lastName,
              roles: mapRoleNamesToReduxRole(d.roles),
            })
          );

          // Store company name for future use
          localStorage.setItem('lastCompanyName', userData.companyName);
          saveRecentCompany(userData.companyName);

          // Log successful biometric login
          await logAuditEvent(
            AUDIT_ACTIONS.USER_LOGIN, 
            `User logged in via biometric authentication - Company: ${userData.companyName}, Email: ${userData.email ?? ''}`
          );

          // Wait longer to ensure state updates are processed
          await new Promise(resolve => setTimeout(resolve, 2000));

          const currentUser = useAuthStore.getState().user;

          if (!currentUser) {
            throw new Error('User data not set in auth store');
          }

          // Make an initial data request to prime the cache
          try {
            const dataResponse = await fetch('https://s3.ts1cloud.com/service3/getLiveData', {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-company-id': userData.companyID.toString(),
                'x-company-name': userData.companyName,
                'Origin': window.location.origin
              }
            });
            
          } catch (error) {
            void error; // non-fatal
            // Continue with navigation even if initial data fetch fails
          }

          // Start the session timeout
          startSessionTimeout();

          // Navigate to home with replace to prevent going back
          navigate('/', { replace: true });
        } else {
          throw new Error('Invalid session data');
        }
      } else {
        throw new Error('Verification failed');
      }
    } catch (error: any) {
      await logAuditEvent(
        AUDIT_ACTIONS.USER_LOGIN,
        `Failed biometric login attempt - Company: ${credentials.companyName}, Error: ${error.name || error.message}`
      );
      
      if (error.name === 'NotAllowedError') {
        if (isIPad()) {
          setError('Biometric authentication was cancelled. On iPad, please ensure you have Face ID or Touch ID enabled and try again.');
        } else {
          setError('Biometric authentication was cancelled or timed out. Please try again.');
        }
      } else if (error.name === 'SecurityError') {
        setError('Security error during biometric login. Please ensure you are using HTTPS.');
      } else if (error.name === 'NotSupportedError') {
        if (isIPad()) {
          setError('Your iPad may not support biometric authentication. Please ensure Face ID or Touch ID is set up and enabled.');
        } else {
          setError('Your device or browser does not support biometric authentication. Please use password login.');
        }
      } else if (error.message.includes('Invalid response format')) {
        setError('Authentication failed: Invalid response format. Please try again or use password login.');
      } else if (error.name === 'InvalidStateError') {
        if (isIPad()) {
          setError('Biometric authentication failed. Please ensure you have registered biometric credentials for this account.');
        } else {
          setError('Biometric authentication failed. Please ensure you have registered biometric credentials for this account.');
        }
      } else {
        setError(`Biometric login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-canvas">
        <div className="text-gray-600">Checking session...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pattern py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-app-panel/80 backdrop-blur-sm p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <Logo className="h-12 w-auto mb-6 text-indigo-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Temperature Monitoring System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}
        {biometricsSupported && !showPasswordForm && (
          <div className="mt-8">
            <button
              onClick={handleBiometricLogin}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Fingerprint className="w-5 h-5 mr-2" />
              Sign in with Biometrics
            </button>
            {isIPad() && (
              <div className="mt-2 text-xs text-gray-600 text-center space-y-1">
                <p>On iPad: Use Face ID or Touch ID when prompted</p>
                <p>If you see "Sign in with iPhone, iPad, or Android device",</p>
                <p>select "This device" and use Face ID/Touch ID</p>
              </div>
            )}
          </div>
        )}
        {(showPasswordForm || !biometricsSupported) && <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="text"
                required
                name="company"
                id="company-name"
                autoComplete="organization"
                list="recent-companies"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Company Name"
                value={credentials.companyName}
                onChange={(e) =>
                  setCredentials({ ...credentials, companyName: e.target.value })
                }
                onBlur={handleCompanyBlur}
              />
              {recentCompanies.length > 0 && (
                <datalist id="recent-companies">
                  {recentCompanies.map(c => <option key={c} value={c} />)}
                </datalist>
              )}
            </div>
            <div>
              <input
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
              />
            </div>
            <div>
              <input
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>}
        <div className="mt-4 text-center space-y-2">
          <button
            type="button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            {showPasswordForm ? 'Use Biometric Login' : 'Use Password Login'}
          </button>
          <div>
            <Link to="/forgot-password" className="text-sm font-medium text-gray-800 hover:text-indigo-700">
              Forgot your password?
            </Link>
          </div>
        </div>
        <footer className="mt-8 text-center text-sm font-medium text-gray-800">
          <div className="space-y-1">
            <div>© {new Date().getFullYear()} Temperature Monitoring System</div>
            <div className="text-xs font-normal text-gray-700">Powered by TS1</div>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs font-normal text-gray-600">
              <Link to="/terms" className="hover:text-indigo-600 hover:underline">
                Terms
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/compliance" className="hover:text-indigo-600 hover:underline">
                Compliance
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/privacy" className="hover:text-indigo-600 hover:underline">
                Privacy
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/cookies" className="hover:text-indigo-600 hover:underline">
                Cookies
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/dpa" className="hover:text-indigo-600 hover:underline">
                DPA
              </Link>
            </div>
            <div className="text-xs font-normal text-gray-500">v{__APP_VERSION__}</div>
          </div>
        </footer>
      </div>
    </div>
  );
}